import { Resolver, useForm } from 'react-hook-form';
import moment, { Moment } from 'moment';
import {
    ApplyNowFormValues,
    applyNowCandidateInformationSchema,
    applyNowReferrerInformationSchema,
    applyNowAdditionalQuestionsSchema,
    emptyApplyNowCandidateInformationForm,
    emptyApplyNowReferrerInformationForm,
    emptyApplyNowAdditionalQuestionsForm,
    IApplyNowCandidateInformationForm,
    IApplyNowReferrerInformationForm,
    IApplyNowAdditionalQuestionsForm,
    IStepperInfo,
    IApplyQuestion,
    QuestionType,
    QuestionAnswerFields,
    IDateRangeAnswer,
    IQuestionAnswerPayload,
} from '../../common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
    Form,
    Grid,
    PargodaRoundButton,
    Step,
    StepLabel,
    Stepper,
    Typography,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../base';
import {
    IApplyToJobAction,
    jobApplicationActions,
    jobApplicationSelectors,
    jobsActions,
    jobsSelectors,
    userSelectors,
} from '../../actions';
import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import TermsAndConditionsCheckbox from '../TermsAndConditionsCheckbox';
import {
    StyledApplyNowBox,
    StyledApplyNowMiddleContainerBox,
    StepConnector,
} from './ApplyNowModal.styles';
import PargodaModal from '../PargodaModal';
import {
    CandidateInformationStep,
    ReferrerInformationStep,
    AdditionalQuestionsStep,
} from './steps';
import CustomStepIcon from './CustomStepIcon';

interface Props {
    positionTitle?: string;
    isModalOpen: boolean;
    setIsModalOpen: (isModalOpen: boolean) => void;
    isEmployerPortal?: boolean;
}
const ApplyNowModalV2 = ({
    positionTitle,
    isModalOpen,
    setIsModalOpen,
    isEmployerPortal = false,
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { id = '0' } = useParams();
    const theme = useTheme();
    const [totalSteps, setTotalSteps] = useState(1);
    const [activeStepIndex, setActiveStepIndex] = useState(0);

    const publishedJobs = useAppSelector(jobsSelectors.getPublishedJobs);
    const activeJob = useAppSelector(jobsSelectors.getActiveJob);

    const [applyQuestions, setApplyQuestions] = useState<IApplyQuestion[]>([]);
    const hasAdditionalQuestions = applyQuestions.length > 0;

    const goToNextStep = () => {
        if (activeStepIndex < totalSteps - 1) {
            setActiveStepIndex((prev) => prev + 1);
        }
    };

    const goToPreviousStep = () => {
        if (activeStepIndex > 0) {
            setActiveStepIndex((prev) => prev - 1);
        }
    };

    const candidateInformationMethods =
        useForm<IApplyNowCandidateInformationForm>({
            resolver: yupResolver(
                applyNowCandidateInformationSchema(t, isEmployerPortal)
            ) as Resolver<IApplyNowCandidateInformationForm>,
            mode: 'onSubmit',
            defaultValues: emptyApplyNowCandidateInformationForm,
        });

    const referrerInformationMethods =
        useForm<IApplyNowReferrerInformationForm>({
            resolver: yupResolver(
                applyNowReferrerInformationSchema(t)
            ) as Resolver<IApplyNowReferrerInformationForm>,
            mode: 'onSubmit',
            defaultValues: emptyApplyNowReferrerInformationForm,
        });

    const additionalQuestionsResolver = useMemo(
        () =>
            yupResolver(
                applyNowAdditionalQuestionsSchema(t, applyQuestions)
            ) as Resolver<IApplyNowAdditionalQuestionsForm>,
        [t, applyQuestions]
    );

    const additionalQuestionsMethods =
        useForm<IApplyNowAdditionalQuestionsForm>({
            resolver: additionalQuestionsResolver,
            mode: 'onSubmit',
            defaultValues: emptyApplyNowAdditionalQuestionsForm,
        });

    const {
        watch: watchCandidateInformation,
        reset: resetCandidateInformation,
        control: controlCandidateInformation,
    } = candidateInformationMethods;
    const {
        reset: resetReferrerInformation,
        control: controlReferrerInformation,
    } = referrerInformationMethods;
    const {
        reset: resetAdditionalQuestions,
        control: controlAdditionalQuestions,
        clearErrors: clearAdditionalQuestionsErrors,
        trigger: triggerAdditionalQuestions,
    } = additionalQuestionsMethods;

    const validateTermsAccepted = async () => {
        return triggerAdditionalQuestions(ApplyNowFormValues.acceptTerms);
    };

    // Keep yup schema in sync when the selected job's questions change
    useEffect(() => {
        additionalQuestionsMethods.control._options.resolver =
            additionalQuestionsResolver;
        clearAdditionalQuestionsErrors();
    }, [
        additionalQuestionsResolver,
        additionalQuestionsMethods.control,
        clearAdditionalQuestionsErrors,
    ]);

    const isReferrer = watchCandidateInformation(ApplyNowFormValues.isReferrer);
    const selectedJobOption = watchCandidateInformation(
        ApplyNowFormValues.jobApplicationId
    );

    useEffect(() => {
        if (isEmployerPortal) {
            if (selectedJobOption?.value) {
                const selectedJob = publishedJobs.find(
                    (job) => job.id === Number(selectedJobOption.value)
                );
                setApplyQuestions(selectedJob?.applyQuestions || []);
            } else {
                setApplyQuestions([]);
            }
            return;
        }

        setApplyQuestions(activeJob?.applyQuestions || []);
    }, [isEmployerPortal, selectedJobOption, publishedJobs, activeJob]);

    const stepperInfo: IStepperInfo = useMemo(() => {
        const steps = [{ labelText: t('applyNow.candidateInformation') }];
        if (isReferrer) {
            steps.push({ labelText: t('applyNow.referrerInformation') });
        }
        if (hasAdditionalQuestions) {
            steps.push({ labelText: t('applyNow.additionalQuestions') });
        }

        return {
            steps,
            activeStepIndex,
        };
    }, [activeStepIndex, hasAdditionalQuestions, isReferrer, t]);

    const isApplyLoadingOrSubmitting = useAppSelector(
        jobApplicationSelectors.getApplyNowLoading
    );

    const jobIdSuccessfullyApplied = useAppSelector(
        jobApplicationSelectors.getJobIdSuccessfullyApplied
    );

    useEffect(() => {
        if (jobIdSuccessfullyApplied) {
            setIsModalOpen(false);
            dispatch(jobApplicationActions.setJobIdSuccessfullyAppliedFalse());
        }
    }, [dispatch, jobIdSuccessfullyApplied, setIsModalOpen]);

    const companyInfo = useAppSelector(userSelectors.getCompanyInfo);

    const publishedJobsOptions = useAppSelector(
        jobsSelectors.getPublishedJobsOptions
    );

    useEffect(() => {
        if (isEmployerPortal) {
            dispatch(jobsActions.fetchPublishedJobsOptions());
        }
    }, [dispatch, isEmployerPortal]);

    useEffect(() => {
        if (isModalOpen) {
            resetCandidateInformation(emptyApplyNowCandidateInformationForm);
            resetReferrerInformation(emptyApplyNowReferrerInformationForm);
            resetAdditionalQuestions(emptyApplyNowAdditionalQuestionsForm);
            setActiveStepIndex(0);
            if (isEmployerPortal) {
                setApplyQuestions([]);
            }
        }
    }, [
        isModalOpen,
        isEmployerPortal,
        resetCandidateInformation,
        resetReferrerInformation,
        resetAdditionalQuestions,
    ]);

    useEffect(() => {
        let steps = 1;
        if (isReferrer) {
            steps += 1;
        }
        if (hasAdditionalQuestions) {
            steps += 1;
        }
        setTotalSteps(steps);
        setActiveStepIndex((prev) => Math.min(prev, steps - 1));
    }, [hasAdditionalQuestions, isReferrer]);

    const transformQuestionAnswers = (): IQuestionAnswerPayload[] => {
        const additionalQuestionsData = additionalQuestionsMethods.getValues();
        const questionAnswers =
            additionalQuestionsData[ApplyNowFormValues.questionAnswers] || [];

        // Backend requires an entry for every active question
        return applyQuestions.map((question) => {
            const qa = questionAnswers.find(
                (item) =>
                    item[QuestionAnswerFields.QUESTION_ID] === question.id
            );
            const answer = qa?.[QuestionAnswerFields.ANSWER];
            const payload: IQuestionAnswerPayload = {
                apply_question: question.id,
            };

            switch (question.type) {
                case QuestionType.SINGLE_SELECT:
                    if (answer && typeof answer === 'string') {
                        payload.selected_option_values = [Number(answer)];
                    }
                    break;
                case QuestionType.MULTIPLE_SELECT:
                    if (Array.isArray(answer) && answer.length > 0) {
                        payload.selected_option_values = answer.map(Number);
                    }
                    break;
                case QuestionType.FILE_UPLOAD:
                    if (answer && answer instanceof File) {
                        payload.file = answer;
                    }
                    break;
                case QuestionType.SELECT_DATE:
                    if (answer) {
                        payload.answer = moment(answer as Date | Moment).format(
                            'YYYY-MM-DD'
                        );
                    }
                    break;
                case QuestionType.DATE_RANGE: {
                    const dateRange = answer as IDateRangeAnswer;
                    if (dateRange?.from && dateRange?.to) {
                        const fromStr = moment(dateRange.from).format(
                            'YYYY-MM-DD'
                        );
                        const toStr = moment(dateRange.to).format(
                            'YYYY-MM-DD'
                        );
                        payload.answer = `${fromStr} to ${toStr}`;
                    }
                    break;
                }
                case QuestionType.BOOLEAN:
                    if (
                        answer !== null &&
                        answer !== undefined &&
                        answer !== ''
                    ) {
                        payload.answer = String(answer);
                    }
                    break;
                default:
                    if (
                        answer !== null &&
                        answer !== undefined &&
                        answer !== ''
                    ) {
                        payload.answer = String(answer);
                    }
                    break;
            }

            return payload;
        });
    };

    const applyNowSubmit = () => {
        const queryParams = new URLSearchParams(location.search);
        const companyKey = queryParams.get('ck') || companyInfo?.key || '';
        const ref = queryParams.get('ref');
        const candidateInformationData =
            candidateInformationMethods.getValues();
        const referrerInformationData = referrerInformationMethods.getValues();
        const additionalQuestionsData = additionalQuestionsMethods.getValues();

        const questionAnswers = transformQuestionAnswers();

        const applyNowActionData: IApplyToJobAction = {
            jobId: isEmployerPortal
                ? candidateInformationData[ApplyNowFormValues.jobApplicationId]
                      ?.value ?? ''
                : id,
            payload: {
                first_name:
                    candidateInformationData[ApplyNowFormValues.firstName],
                last_name:
                    candidateInformationData[ApplyNowFormValues.lastName],
                phone: candidateInformationData[ApplyNowFormValues.phoneNumber],
                email: candidateInformationData[ApplyNowFormValues.email],
                resume: {
                    file: candidateInformationData[ApplyNowFormValues.file],
                    accepted_terms:
                        additionalQuestionsData[ApplyNowFormValues.acceptTerms],
                },
                notes:
                    referrerInformationData[ApplyNowFormValues.coverLetter] ||
                    undefined,
                ref: ref || undefined,
                company_key: companyKey,
                referrer: candidateInformationData[
                    ApplyNowFormValues.isReferrer
                ]
                    ? {
                          first_name:
                              referrerInformationData[
                                  ApplyNowFormValues.referrerName
                              ],
                          last_name:
                              referrerInformationData[
                                  ApplyNowFormValues.referrerLastName
                              ],
                          phone: referrerInformationData[
                              ApplyNowFormValues.referrerPhoneNumber
                          ],
                          company_name:
                              referrerInformationData[
                                  ApplyNowFormValues.referrerCompany
                              ],
                          email: referrerInformationData[
                              ApplyNowFormValues.referrerEmail
                          ],
                          is_point_of_contact: true,
                      }
                    : undefined,
                question_answers:
                    questionAnswers.length > 0 ? questionAnswers : undefined,
            },
        };
        dispatch(jobApplicationActions.applyToJob(applyNowActionData));
    };

    const onCandidateInformationSubmit = async () => {
        if (!isReferrer && !hasAdditionalQuestions) {
            const termsAccepted = await validateTermsAccepted();
            if (!termsAccepted) {
                return;
            }
            applyNowSubmit();
            return;
        }
        goToNextStep();
    };

    const onReferrerInformationSubmit = async () => {
        if (!hasAdditionalQuestions) {
            const termsAccepted = await validateTermsAccepted();
            if (!termsAccepted) {
                return;
            }
            applyNowSubmit();
            return;
        }
        goToNextStep();
    };

    const onAdditionalQuestionsSubmit = () => {
        applyNowSubmit();
    };

    const referrerStepIndex = isReferrer ? 1 : -1;
    const additionalQuestionsStepIndex = hasAdditionalQuestions
        ? isReferrer
            ? 2
            : 1
        : -1;
    const showTermsOnCandidateStep = !isReferrer && !hasAdditionalQuestions;
    const showTermsOnReferrerStep = isReferrer && !hasAdditionalQuestions;

    const renderTermsCheckbox = () => (
        <TermsAndConditionsCheckbox
            control={controlAdditionalQuestions}
            name={ApplyNowFormValues.acceptTerms}
        />
    );

    const renderHeader = () => (
        <Grid container>
            <Grid item xs={12} pb={2.25}>
                <Typography variant={'h6'} color={theme.palette.grey[600]}>
                    {isEmployerPortal
                        ? t('applyNow.titleManual')
                        : t('applyNow.title', {
                              positionTitle: positionTitle,
                              interpolation: { escapeValue: false },
                          })}
                </Typography>
            </Grid>
            {stepperInfo.steps.length > 1 && (
                <Grid item xs={12} pb={2}>
                    <Stepper
                        activeStep={activeStepIndex}
                        alternativeLabel
                        connector={<StepConnector />}
                    >
                        {stepperInfo.steps.map((step, index) => (
                            <Step key={step.labelText}>
                                <StepLabel
                                    StepIconComponent={() => (
                                        <CustomStepIcon
                                            isActive={index === activeStepIndex}
                                            isCompleted={
                                                index < activeStepIndex
                                            }
                                        />
                                    )}
                                >
                                    <Typography
                                        variant="caption"
                                        style={{
                                            color: theme.palette.grey[600],
                                        }}
                                    >
                                        {step.labelText}
                                    </Typography>
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Grid>
            )}
        </Grid>
    );

    const renderButtons = () => (
        <Grid container>
            <Grid item xs={12}>
                <Grid container>
                    <Grid
                        item
                        xs={6}
                        display={'flex'}
                        justifyContent={'flex-end'}
                        pb={2}
                    >
                        <PargodaRoundButton
                            textColor={theme.palette.grey[600]}
                            variant={'outlined'}
                            loadingPosition={'start'}
                            startIcon={<></>}
                            onClick={() => {
                                if (stepperInfo.activeStepIndex !== 0) {
                                    goToPreviousStep();
                                } else {
                                    setIsModalOpen(false);
                                }
                            }}
                            loading={isApplyLoadingOrSubmitting}
                            sx={{
                                marginInlineEnd: theme.spacing(1.5),
                                height: '48px',
                                width: '214px',
                                fontWeight: 600,
                                fontSize: '16px',
                            }}
                        >
                            {stepperInfo.activeStepIndex > 0
                                ? t('applyNow.back')
                                : t('applyNow.applyNowCancelButton')}
                        </PargodaRoundButton>
                    </Grid>
                    <Grid item xs={6} display={'flex'} pb={2}>
                        <PargodaRoundButton
                            textColor={theme.palette.common.white}
                            variant={'contained'}
                            type={'submit'}
                            loadingPosition={'start'}
                            startIcon={<></>}
                            loading={isApplyLoadingOrSubmitting}
                            sx={{
                                background: `linear-gradient(0deg,${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                width: '214px',
                                fontWeight: 600,
                                fontSize: '16px',
                            }}
                        >
                            {stepperInfo.activeStepIndex <
                            stepperInfo.steps.length - 1
                                ? t('applyNow.next')
                                : t('applyNow.applyNowButton')}
                        </PargodaRoundButton>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );

    return (
        <PargodaModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            sx={{
                width: '595px',
                maxWidth: '100%',
                padding: theme.spacing(3.5),
            }}
        >
            <StyledApplyNowBox>
                {renderHeader()}
                {activeStepIndex === 0 && (
                    <Form
                        methods={candidateInformationMethods}
                        onSubmit={onCandidateInformationSubmit}
                    >
                        <StyledApplyNowMiddleContainerBox>
                            <CandidateInformationStep
                                control={controlCandidateInformation}
                                isEmployerPortal={isEmployerPortal}
                                publishedJobsOptions={publishedJobsOptions}
                            />
                            {showTermsOnCandidateStep && renderTermsCheckbox()}
                        </StyledApplyNowMiddleContainerBox>
                        {renderButtons()}
                    </Form>
                )}
                {activeStepIndex === referrerStepIndex && isReferrer && (
                    <Form
                        methods={referrerInformationMethods}
                        onSubmit={onReferrerInformationSubmit}
                    >
                        <StyledApplyNowMiddleContainerBox>
                            <ReferrerInformationStep
                                control={controlReferrerInformation}
                            />
                            {showTermsOnReferrerStep && renderTermsCheckbox()}
                        </StyledApplyNowMiddleContainerBox>
                        {renderButtons()}
                    </Form>
                )}
                {activeStepIndex === additionalQuestionsStepIndex &&
                    hasAdditionalQuestions && (
                        <Form
                            methods={additionalQuestionsMethods}
                            onSubmit={onAdditionalQuestionsSubmit}
                        >
                            <StyledApplyNowMiddleContainerBox>
                                <AdditionalQuestionsStep
                                    applyQuestions={applyQuestions}
                                    control={controlAdditionalQuestions}
                                />
                                {renderTermsCheckbox()}
                            </StyledApplyNowMiddleContainerBox>

                            {renderButtons()}
                        </Form>
                    )}
            </StyledApplyNowBox>
        </PargodaModal>
    );
};

export default ApplyNowModalV2;
